import { css } from '@emotion/react';
import { useQuery } from '@apollo/client';
import { Avatar, SelectItemProps } from '@mantine/core';
import { Form } from '@rr/ui';
import { IconSchool, IconBuilding } from '@tabler/icons';

import { AUTOCOMPLETE_LOCATION_QUERY } from './AutocompleteLocation.query.gql';
import { AutocompleteLocationQuery } from 'types/graphql';

import { forwardRef } from 'react';
import { string } from 'yup/lib/locale';

interface AutocompleteLocationProps {
  label: string;
  name: string;
  value?: {
    value: string;
    label: string;
  };
  multiple?: boolean;
}

interface ItemProps extends SelectItemProps {
  type: string;
  name: string;
}

const AutocompleteItem = forwardRef<HTMLDivElement, ItemProps>(
  ({ value, label, type, ...rest }: ItemProps, ref) => {
    const styles = css`
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px;
    `;

    return (
      <div css={styles} ref={ref} {...rest}>
        <Avatar color="blue" radius="xl" size="sm">
          {type === 'SCHOOL' ? (
            <IconSchool size={17} />
          ) : (
            <IconBuilding size={17} />
          )}
        </Avatar>
        <div>{label}</div>
      </div>
    );
  }
);

export const AutocompleteLocation = ({
  label,
  value,
  multiple,
  ...props
}: AutocompleteLocationProps) => {
  const { data, error, loading } = useQuery<AutocompleteLocationQuery>(
    AUTOCOMPLETE_LOCATION_QUERY
  );

  if (error) {
    console.error(error);
    return <div>Error! Please check the console.</div>;
  }

  const locations = data?.locations.map((location) => ({
    value: location.id,
    label: location.name,
    type: location.location_type,
  })) ?? ['- No data provided -'];

  return multiple ? (
    <Form.MultiSelect
      label={label}
      itemComponent={AutocompleteItem}
      data={locations}
      disabled={loading}
      searchable
      {...props}
    />
  ) : (
    <Form.ACField
      label={label}
      itemComponent={AutocompleteItem}
      data={value ?? locations}
      disabled={loading}
      searchable
      {...props}
    />
  );
};
